import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Loader2, Check, Star, AlertTriangle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from 'src/components/ui/dialog';
import { Button } from 'src/components/ui/button';
import { Input } from 'src/components/ui/input';
import { Label } from 'src/components/ui/label';
import { Textarea } from 'src/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'src/components/ui/select';
import { useLocationMap } from 'src/services/api/queries/referenceDataQueries';
import { useRequirementLocations } from 'src/services/api/queries/opportunityQueries';
import {
  useCandidateSkillMatch,
  useCandidateProfile,
} from 'src/services/api/queries/candidateQueries';
import { useUpdateCandidateSkillMatch } from 'src/services/api/mutations/candidateMutations';
import { useToast } from 'src/hooks/use-toast';
import { cn, normalizeCtcToLpa } from 'src/lib/utils';

// "Update Profile Details" (senior_level_flow) — captures what the candidate
// shared on a first-contact call. Field set, grouping, spacing and typography
// are kept in sync with the reference "Capture details" mockup's own CSS
// (`.mtabs`/`.mtab`, `.fld`, `.skl`/`.skl-pair`/`.stars`/`.seg`, `.pills`) —
// pixel values translated to this app's Dialog/Input/Select primitives and
// ds-*/Tailwind tokens rather than the mockup's raw CSS custom properties,
// but matched closely rather than loosely approximated: the tab strip below
// is a bespoke underline bar (not the generic shadcn `Tabs` pill control)
// because the mockup's `.mtabs` — full-bleed, gap-20px, 2px underline,
// mono count pills — is a distinct visual signature from that generic
// control. A Details pane (CTC pair, then notice period + total experience
// side by side, then preferred locations as toggleable pills, then a
// free-text note) and a Skills pane (per-skill experience/proficiency/
// last-used, added on the fly as they come up on the call). The mockup's
// third pane (Schedule/Interview — booking a slot on the same call) stays
// out of scope, per explicit instruction; only Details + Skills are built
// here.
//
// Its location pills mark which ones are "advertised" on the requirement (a
// small star, see LocationPills' own comment below) AND tick them by
// default on prefill (see buildDetailsFromProfile) — needed threading
// assignmentId the rest of the way down the action-component chain
// (GenericFlowAction → RowActionsCell/ManualActionsBar →
// SelectableTabTable/seniorFlowTabFactory), mirroring how candidateId/
// opportunityId were threaded for the Details-tab profile prefill above.
//
// The mockup's other apparent simplification — one combined Save instead of
// its own three independent per-pane saves — turned out not to be one:
// confirmed live, Details and Skills really do post to two entirely
// different endpoints with two different shapes (see below), so each tab
// now has its own Save button and its own submit path, matching the
// mockup's own reasoning ("its own backend takes one write per pane") after
// all. Save details doesn't close the dialog — it switches to the Skills
// tab instead (see handleSubmitDetails' own comment), since there's a
// second save right there the TA still needs to get to. Save skills DOES
// close the dialog (unchanged) — it's the last pane, so closing is the
// actual "done" point once it's fired.
//
// Skills is genuinely locked (disabled tab button, not just visually
// muted) until Save details has actually fired at least once this opening
// — per explicit request, rather than the tab being freely clickable at
// any time the way it was before. See `detailsSaved` below.
//
// The Skills pane's other mockup-vs-here gap — a live "Required for this
// role" list — IS built: GET /internal/assignments/candidates-pipeline/
// :secMappingId/skill-match returns the requirement's required_skills (each with the candidate's own
// evidenced proficiency/experience/recency where already known) plus
// extra_core_skills the candidate has beyond what's required. Only fetched
// when exactly one candidate is selected (GenericFlowAction passes
// secMappingId as null for a bulk capture — see its own comment) since the
// endpoint is single-candidate; a bulk capture's Skills tab falls back to
// the flat, manual-add-only list this component started with. Two mapping
// assumptions here are unconfirmed against the live enum and flagged for
// live verification rather than silently trusted, per this app's own
// track record of sample-vs-live drift on this feature area:
//   - candidate_proficiency / expected_proficiency: assumed BEGINNER /
//     INTERMEDIATE / ADVANCED / EXPERT → 1–4 stars, matching this exact
//     4-tier vocabulary already used elsewhere in the reference mockup's
//     own candidate-profile rendering (PROF_MAX=4, same reasoning: an odd
//     scale lets people not commit). An unrecognised string maps to 0
//     (unrated) rather than guessing a rank.
//   - extra_core_skills' item shape: the sample response's own example was
//     always `[]`, so its fields are inferred from required_skills' shape
//     (same `name` key) rather than confirmed against a populated response.
//
// Both panes' contracts are now confirmed live — no more guessing:
//
// Details tab → POST .../flow-actions/interview_profile_update (fired via
// the usual onSubmit → GenericFlowAction.runAction → executeFlowAction path,
// which is what adds the outer `{ payload: ... }` wrapper), with:
//   { payload: { section: "interview_profile_update",
//                parsed_data: { current_ctc, expected_ctc, notice_period,
//                  total_work_experience, preferred_work_locations: [ids],
//                  notes } } }
// So `section` is the action key itself, not one of the earlier-suspected
// generic enum values (contact/compensation/personal/skills/...) — those
// were a plausible-looking dead end from an early probe, not the real shape.
//
// Skills tab → its own endpoint entirely, PATCH .../skill-match (same URL
// the GET prefill above reads from), independent of flow-actions — see
// handleSubmitSkills' own comment for the full payload shape (method and
// shape both changed from an earlier POST-based contract, confirmed live
// via a real 422 against the old one). Fired directly from this component
// via useUpdateCandidateSkillMatch, bypassing the flow-action system entirely
// since it isn't a flow action.
//
// Details tab prefill: reuses GET /internal/candidates/:id/profile
// (?requirement_id=) — the exact same call/hook CandidateProfileModal.jsx
// already makes — rather than a new endpoint, per explicit instruction.
// Needs the candidate's own candidate_id (distinct from secMappingId) and
// optionally opportunityId; both now threaded down from GenericFlowAction
// (candidateIds prop, mirroring its existing mappingIds), which in turn
// gets them from RowActionsCell (single-row kebab: candidateId/opportunityId
// were already available there for "View profile", just not forwarded
// before) and SelectableTabTable (bulk bar: candidateIds mirrors its
// existing mappingIds, built off the same selectedIds — which already *are*
// candidate_ids). Same single-candidate-only gating as the Skills tab's
// prefill: null for a bulk capture, and the Details tab just renders blank
// in that case (buildDetailsFromProfile below only patches keys the
// response actually has, so there's genuinely nothing to prefill against,
// not a broken fetch).
const NOTICE_OPTIONS = [
  { value: '0', label: 'Immediate' },
  { value: '15', label: '15 days' },
  { value: '30', label: '30 days' },
  { value: '45', label: '45 days' },
  { value: '60', label: '60 days' },
  { value: '90', label: '90 days' },
];

// Matches the mockup's `.fld > label` treatment (§27 field labels) — small
// caps, tracked, muted — applied to every field label here for the same
// reason the mockup keeps one label style: five different fields reading as
// one form rather than five ad-hoc ones.
const FIELD_LABEL_CLS =
  'block text-[10.5px] font-[650] uppercase tracking-[0.05em] text-muted-foreground';

// Four, not five, per the mockup's own reasoning: an odd scale gives a
// candidate a middle to sit in, and "3 out of 5" is the answer people give
// when they don't want to commit. Four forces a side of the halfway mark.
const PROFICIENCY_MAX = 4;

// "Stale" drives the amber tint on the pressed pill, because a skill last
// touched three years ago is worth an interviewer's question even when the
// candidate rates it top marks — the gap between claimed proficiency and
// last use is the single most useful thing this pane produces.
const LAST_USED_OPTIONS = [
  { value: 'current', label: 'Currently using', stale: false },
  { value: 'within_year', label: 'Within a year', stale: false },
  { value: '1_3_years', label: '1–3 years ago', stale: true },
  { value: '3_plus_years', label: '3+ years ago', stale: true },
];

// This form's own lastUsed bucket values → the skill-match PATCH's real
// `recency` enum — confirmed live via an actual 422 against a first guess
// (that it reused these bucket strings verbatim; it doesn't — different
// casing AND different vocabulary for the two "years ago" buckets).
const RECENCY_BY_LAST_USED = {
  current: 'CURRENT',
  within_year: 'WITHIN_1Y',
  '1_3_years': 'WITHIN_3Y',
  '3_plus_years': 'OVER_3Y',
};

const DETAILS_FIELD_COUNT = 5;

const emptyForm = {
  currentCtc: '',
  expectedCtc: '',
  noticeDays: '',
  expYears: '',
  expMonths: '',
  locations: [],
  notes: '',
  skills: [],
};

const emptySkill = (name, required = false) => ({
  name,
  required,
  years: '',
  months: '',
  proficiency: 0,
  lastUsed: '',
  // Only ever set by skillFromApiItem below (a required skill straight off
  // the skill-match response) — null here for anything the TA types in by
  // hand, since there's no requirement row to read an expectation from.
  expectedProficiency: null,
  expectedExperienceMonths: null,
  // The skill-match PATCH now requires a `type` per skill (DOMAIN_SKILL/
  // TOOL/SOFT_SKILL/REGULATORY) — see handleSubmitSkills' own comment.
  // null here for a skill the TA typed in by hand: there's no reference
  // skill row to read a type from, unlike skillFromApiItem below.
  type: null,
});

// A skill counts as captured once we know how good they say they are AND
// when they last touched it — years are useful context but optional,
// mirroring the mockup's own capSkillFilled.
const skillFilled = (skill) => !!(skill.proficiency && skill.lastUsed);

// True the moment ANY of a skill's own fields has something in it —
// experience, proficiency, or last-used. Paired with skillFilled (above) to
// catch the partial-entry case per explicit request: a skill the TA has
// started answering (e.g. years + last-used typed in) but hasn't finished
// (no proficiency picked yet) shouldn't be silently dropped from the save
// the way a completely untouched skill safely is — see hasIncompleteSkills
// and SkillCard's own "incomplete" styling below.
const skillStarted = (skill) =>
  !!(skill.years || skill.months || skill.proficiency || skill.lastUsed);

// See the file-level comment's first mapping assumption. Unrecognised
// strings fail to 0 (unrated) rather than a guessed rank.
const PROFICIENCY_RANK = {
  BEGINNER: 1,
  INTERMEDIATE: 2,
  ADVANCED: 3,
  EXPERT: 4,
};

// Inverse of the map above — this form's own 1–4 star rank back to the
// skill-match PATCH's required enum string. Only ever looked up for a
// skillFilled() skill (proficiency is always 1–4 by then, never 0), so
// every real lookup hits; no fallback branch needed.
const PROFICIENCY_LABEL_BY_RANK = {
  1: 'BEGINNER',
  2: 'INTERMEDIATE',
  3: 'ADVANCED',
  4: 'EXPERT',
};

// Buckets the API's `candidate_is_currently_used` / `candidate_last_used_date`
// into this form's four last-used values. "Currently using" wins outright;
// otherwise the gap since the recorded date decides the bucket. No date at
// all means the candidate's own recency is simply unknown yet — the TA can
// still fill it in from the call, same as any manually-added skill.
const lastUsedBucketFromApi = (item) => {
  if (item.candidate_is_currently_used) return 'current';
  if (!item.candidate_last_used_date) return '';
  const last = new Date(item.candidate_last_used_date);
  if (Number.isNaN(last.getTime())) return '';
  const monthsSince =
    (Date.now() - last.getTime()) / (1000 * 60 * 60 * 24 * 30.44);
  if (monthsSince < 12) return 'within_year';
  if (monthsSince < 36) return '1_3_years';
  return '3_plus_years';
};

// One skill-match API item (required or extra) → this form's skill shape.
// `candidate_has_skill: false` still produces a card — for a required skill
// that's the whole point (an absence is the thing worth asking about); for
// an extra skill it just starts blank, same as one the TA types in by hand.
const skillFromApiItem = (item, required) => {
  const months = item.candidate_experience_months;
  return {
    name: item.name,
    required,
    years: months != null ? String(Math.floor(months / 12)) : '',
    months: months != null ? String(months % 12) : '',
    proficiency: PROFICIENCY_RANK[item.candidate_proficiency] || 0,
    lastUsed: lastUsedBucketFromApi(item),
    // What the requirement itself asks for — distinct from the
    // candidate_* fields above, which are the candidate's own evidenced
    // answer. Only required_skills rows carry these (extra_core_skills has
    // no requirement row to read an expectation from, same reasoning as
    // emptySkill above) — null on either field just means the requirement
    // didn't specify one (e.g. Basel III/Stress Testing in a real sample
    // response had is_mandatory: true but both expected_* fields null).
    expectedProficiency: item.expected_proficiency || null,
    expectedExperienceMonths: item.expected_experience_months ?? null,
    // Every required_skills/extra_core_skills item in a real response
    // carries its own `type` (DOMAIN_SKILL/TOOL/SOFT_SKILL/REGULATORY,
    // confirmed live) — carried straight through so handleSubmitSkills can
    // send it back on the PATCH.
    type: item.type || null,
  };
};

// Formats the two fields above into one muted "what's expected" pill shown
// on a required SkillCard, e.g. "Advanced · 7yrs" — surfaces the bar the
// candidate needs to clear right next to the field capturing whether they
// do, rather than making the TA cross-reference the JD separately. Skips
// cleanly when a piece is missing (see skillFromApiItem's comment) instead
// of rendering a stray "· 7yrs" or "Advanced ·".
const formatExpectedProficiency = (proficiency) =>
  proficiency ? proficiency.charAt(0) + proficiency.slice(1).toLowerCase() : '';

const formatExpectedExperience = (months) => {
  if (months == null) return '';
  const y = Math.floor(months / 12);
  const m = months % 12;
  if (y && m) return `${y}y ${m}mo`;
  if (y) return `${y}yr${y > 1 ? 's' : ''}`;
  return `${m}mo`;
};

const expectedRequirementLabel = (skill) =>
  [
    formatExpectedProficiency(skill.expectedProficiency),
    formatExpectedExperience(skill.expectedExperienceMonths),
  ]
    .filter(Boolean)
    .join(' · ');

// Required skills first (so the group that decides whether the candidate
// clears the bar renders first), then extra core skills — matches the
// mockup's own "Required for this role" / "Their other skills" order.
const buildSkillsFromMatch = (data) => [
  ...(data?.required_skills || []).map((item) => skillFromApiItem(item, true)),
  ...(data?.extra_core_skills || []).map((item) =>
    skillFromApiItem(item, false)
  ),
];

// Details-tab prefill — reuses the same GET /internal/candidates/:id/profile
// (?requirement_id=) call CandidateProfileModal.jsx already makes, rather
// than a bespoke endpoint. Its `profile_data.personal_information` /
// `.other_details` fields happen to already use this action's own
// parsed_data vocabulary (current_ctc, expected_ctc, notice_period,
// preferred_work_locations, total_work_experience — see buildDetailsPayload
// below), so this is a direct field-for-field mapping, not a translation.
// `notes` has no equivalent on the profile — that field only ever holds what
// the TA typed on this call, so it's left for the TA to fill, never prefilled.
// Only sets keys the response actually has (same "don't overwrite with a
// blank" discipline buildSkillsFromMatch's sibling functions use) — a
// currently-empty field just means the candidate's own profile hasn't
// captured that yet, not that it should show 0/blank on top of nothing.
//
// Preferred locations are the one field that isn't purely the candidate's
// own answer: the requirement's advertised location(s) (advertisedIds, a
// Set of string ids — see the star treatment in LocationPills) are ticked
// by default too, merged in with whatever the candidate's own profile
// lists, per explicit request. The TA can still deselect one; this is just
// the starting point, on the reasoning that a candidate being put forward
// for this requirement is at minimum open to its own location.
// The form's CTC fields are in LPA (placeholder literally reads "₹ LPA").
// GET /candidates/:id/profile's current_ctc/expected_ctc turned out NOT to
// consistently be one unit or the other — some candidate profiles come back
// already in LPA (e.g. 20), others in plain rupees (e.g. 2000000) despite
// being the same field on the same endpoint. An earlier version of this
// prefill trusted "always LPA" outright (confirmed against one sample that
// happened to already be small) and stringified the raw value — which quietly
// showed "2000000" in a field labelled "₹ LPA" for any record using the other
// unit. normalizeCtcToLpa (src/lib/utils.js) applies the same defensive
// large-number heuristic InterviewBrief.jsx's own formatCtc already uses for
// this exact ambiguity, so both units land correctly regardless of which one
// a given record happens to use. NOT the same contract as buildDetailsPayload's
// own outgoing parsed_data below (POST .../flow-actions/interview_profile_update)
// — that's a different endpoint with no evidence its own unit changed, so its
// *100000 stays untouched; only this GET-side prefill needed the fix.
const buildDetailsFromProfile = (
  profileData,
  locationMap,
  advertisedIds = new Set()
) => {
  const pi = profileData?.personal_information || {};
  const od = profileData?.other_details || {};
  const patch = {};
  const currentCtcLpa = normalizeCtcToLpa(pi.current_ctc);
  if (currentCtcLpa != null) patch.currentCtc = String(currentCtcLpa);
  const expectedCtcLpa = normalizeCtcToLpa(pi.expected_ctc);
  if (expectedCtcLpa != null) patch.expectedCtc = String(expectedCtcLpa);
  if (pi.notice_period != null) patch.noticeDays = String(pi.notice_period);
  if (od.total_work_experience != null) {
    patch.expYears = String(Math.floor(od.total_work_experience / 12));
    patch.expMonths = String(od.total_work_experience % 12);
  }
  const preferredIds = (pi.preferred_work_locations || []).map(String);
  const mergedIds = [...new Set([...preferredIds, ...advertisedIds])];
  if (mergedIds.length) {
    patch.locations = mergedIds.map((locId) => ({
      value: locId,
      label: locationMap[locId] || locId,
    }));
  }
  return patch;
};

// Hidden per explicit request — "not required now" — not removed, since the
// mockup's own reasoning for it ("the candidate who will only move to
// Coimbatore is a real answer, not an input error") still holds; just
// toggle this back on to restore it. addCustom/customInput below stay
// wired so re-enabling is a one-line change, not a rebuild.
const SHOW_ADD_LOCATION_INPUT = false;

// Toggleable rounded pills, matching the mockup's `.pill` component — one
// press per location rather than opening a dropdown, since the whole point
// (per the mockup's own comment) is "tick every one they would take" while
// on a call, without breaking eye contact with a menu. The trailing dashed
// pill accepts a location not on the reference list, same as the mockup:
// "the candidate who will only move to Coimbatore is a real answer, not an
// input error" — currently hidden, see SHOW_ADD_LOCATION_INPUT above.
//
// advertisedIds marks which of `options` are this requirement's own
// advertised locations (GET .../assignment-locations, matched by
// location_id — the same id the reference location table itself uses, per
// a real response: {"location_id":614,...,"location_name":"Mumbai"} against
// this exact requirement) with a small filled star, so the TA can see at a
// glance which of the candidate's picks are actually on the job vs. just
// somewhere they'd also take. `fill-current` rather than a fixed color so
// it reads correctly against both pill states (muted text at rest, white
// text once selected) without a second color decision.
const LocationPills = ({ options, value, onChange, advertisedIds }) => {
  const [customInput, setCustomInput] = useState('');
  const selectedValues = new Set(value.map((v) => v.value));
  // Selected pills typed in via the custom input won't be in `options`
  // (the reference location table) — they still need their own pill.
  const extraSelected = value.filter(
    (v) => !options.some((o) => o.value === v.value)
  );

  const toggle = (option) => {
    onChange(
      selectedValues.has(option.value)
        ? value.filter((v) => v.value !== option.value)
        : [...value, option]
    );
  };

  const addCustom = () => {
    const label = customInput.trim();
    if (!label) return;
    const existing = options.find(
      (o) => o.label.toLowerCase() === label.toLowerCase()
    );
    const option = existing || { value: label, label };
    if (!selectedValues.has(option.value)) onChange([...value, option]);
    setCustomInput('');
  };

  const pillCls = (selected) =>
    cn(
      'inline-flex items-center gap-1.5 rounded-full border px-3 py-[6px] text-xs font-medium transition-colors',
      selected
        ? 'border-primary bg-primary text-primary-foreground'
        : 'border-input bg-card text-muted-foreground hover:border-primary hover:text-primary'
    );

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {options.map((option) => {
        const selected = selectedValues.has(option.value);
        const advertised = advertisedIds.has(option.value);
        return (
          <button
            key={option.value}
            type="button"
            onClick={() => toggle(option)}
            aria-pressed={selected}
            className={pillCls(selected)}
          >
            {selected && <Check className="h-3 w-3" />}
            {advertised && (
              <Star className="h-3 w-3 fill-current" strokeWidth={1.8} />
            )}
            {option.label}
          </button>
        );
      })}
      {extraSelected.map((option) => (
        <button
          key={option.value}
          type="button"
          onClick={() => toggle(option)}
          aria-pressed="true"
          className={pillCls(true)}
        >
          <Check className="h-3 w-3" />
          {option.label}
        </button>
      ))}
      {SHOW_ADD_LOCATION_INPUT && (
        <span className="inline-flex items-center rounded-full border border-dashed border-input bg-card pl-3 pr-1">
          <input
            type="text"
            value={customInput}
            onChange={(e) => setCustomInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addCustom();
              }
            }}
            placeholder="Add another…"
            aria-label="Add a location not listed"
            className="w-28 bg-transparent py-[6px] text-xs outline-none placeholder:text-muted-foreground"
          />
        </span>
      )}
    </div>
  );
};

LocationPills.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({ value: PropTypes.string, label: PropTypes.string })
  ).isRequired,
  value: PropTypes.arrayOf(
    PropTypes.shape({ value: PropTypes.string, label: PropTypes.string })
  ).isRequired,
  onChange: PropTypes.func.isRequired,
  advertisedIds: PropTypes.instanceOf(Set),
};

LocationPills.defaultProps = {
  advertisedIds: new Set(),
};

// "n/total" count chip on each tab trigger, matching the mockup's `.mtab .n`
// (mono, 10px, 700, a 2px/6px pill) — muted by default, brand-tinted while
// its tab is active, green once everything on that tab is captured. Mirrors
// the mockup's own cascade: `.n.done` is declared after `[aria-selected] .n`
// so a completed tab reads green even while active, not brand.
const TabCount = ({ done, total, active }) => {
  const complete = total > 0 && done === total;
  let toneCls = 'bg-muted text-muted-foreground';
  if (complete) {
    toneCls =
      'bg-green-100 dark:bg-green-950/50 text-green-800 dark:text-green-400';
  } else if (active) {
    toneCls = 'bg-brand-bg-subtle text-primary';
  }
  return (
    <span
      className={cn(
        'font-mono text-[10px] font-bold px-1.5 py-[2px] rounded-full',
        toneCls
      )}
    >
      {done}/{total}
    </span>
  );
};

TabCount.propTypes = {
  done: PropTypes.number.isRequired,
  total: PropTypes.number.isRequired,
  active: PropTypes.bool,
};

TabCount.defaultProps = {
  active: false,
};

// 1–4 star proficiency picker, matching the mockup's `.stars` exactly: a
// 25×25 hit area per star (not just the glyph) so the row is easy to land on
// mid-call, unlit stars sit at the border/track tone rather than a faded
// amber, and clicking the already-selected star clears the rating, same as
// the mockup's own toggle-off behaviour.
const StarRating = ({ value, onChange, label }) => (
  <div className="inline-flex items-center" role="group" aria-label={label}>
    {Array.from({ length: PROFICIENCY_MAX }, (_, i) => i + 1).map((n) => (
      <button
        key={n}
        type="button"
        onClick={() => onChange(value === n ? 0 : n)}
        aria-pressed={value === n}
        aria-label={`${n} of ${PROFICIENCY_MAX}`}
        className={cn(
          'inline-flex h-[25px] w-[25px] items-center justify-center transition-colors',
          n <= value ? 'text-amber-500' : 'text-border hover:text-amber-400'
        )}
      >
        <Star
          className="h-[17px] w-[17px]"
          strokeWidth={1.8}
          fill={n <= value ? 'currentColor' : 'none'}
        />
      </button>
    ))}
    <span className="ml-1.5 font-mono text-[11px] font-bold text-muted-foreground">
      {value ? `${value}/${PROFICIENCY_MAX}` : '—'}
    </span>
  </div>
);

StarRating.propTypes = {
  value: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string.isRequired,
};

// Names whichever of the two skillFilled fields a started-but-incomplete
// skill is still missing, for the inline warning below — years/months are
// never listed since they're not part of skillFilled's own definition.
const missingSkillFields = (skill) => {
  const missing = [];
  if (!skill.proficiency) missing.push('a proficiency rating');
  if (!skill.lastUsed) missing.push('when it was last used');
  return missing;
};

// One skill's card: experience (y/m) and proficiency (stars) sit on one row
// because they're one judgement — "how long, how well" — asked and answered
// in a single breath, same pairing the mockup uses. Last used gets its own
// row underneath, since it's the answer that most often contradicts the
// other two.
const SkillCard = ({ skill, onChange }) => {
  const filled = skillFilled(skill);
  // Per explicit request: a skill can't be saved half-answered — once any
  // field has something in it, every field skillFilled checks has to.
  // Flagged right on the card (amber border + inline "add X" hint) rather
  // than only surfacing as a disabled Save button elsewhere, so the TA
  // sees exactly which skill and which field is blocking them.
  const incomplete = !filled && skillStarted(skill);
  const set = (patch) => onChange({ ...skill, ...patch });
  const expectedLabel = skill.required ? expectedRequirementLabel(skill) : '';

  return (
    <div
      className={cn(
        'rounded-[10px] border p-3 transition-colors',
        filled && 'border-primary/30 bg-primary/[0.03]',
        incomplete &&
          'border-amber-400 bg-amber-50 dark:border-amber-500/50 dark:bg-amber-950/20',
        !filled && !incomplete && 'border-border bg-card'
      )}
    >
      <div className="flex items-center gap-[7px] flex-wrap mb-[11px]">
        <span className="text-[13px] font-[650] text-foreground-strong">
          {skill.name}
        </span>
        {expectedLabel && (
          <span className="inline-flex items-center rounded-full bg-muted px-2 py-[2px] text-[10.5px] font-[600] text-muted-foreground">
            {expectedLabel}
          </span>
        )}
        {filled && (
          <span className="ml-auto inline-flex items-center gap-1 text-[10.5px] font-[650] text-green-700 dark:text-green-400">
            <Check className="h-[13px] w-[13px]" strokeWidth={2.6} />
            Captured
          </span>
        )}
        {incomplete && (
          <span className="ml-auto inline-flex items-center gap-1 text-[10.5px] font-[650] text-amber-700 dark:text-amber-400">
            <AlertTriangle className="h-[13px] w-[13px]" strokeWidth={2.4} />
            Incomplete
          </span>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-x-[22px] gap-y-2 mb-2.5">
        <div className="flex items-center gap-2.5">
          <span className="text-[10px] font-[650] uppercase tracking-[0.05em] text-muted-foreground">
            Experience
          </span>
          <div className="inline-flex items-center gap-[5px]">
            <Input
              type="number"
              min="0"
              max="45"
              aria-label={`${skill.name} — years`}
              className="w-12 h-7 px-1 text-center font-mono text-xs font-semibold"
              value={skill.years}
              onChange={(e) => set({ years: e.target.value })}
            />
            <span className="text-[11.5px] text-muted-foreground">y</span>
            <Input
              type="number"
              min="0"
              max="11"
              aria-label={`${skill.name} — months`}
              className="w-12 h-7 px-1 text-center font-mono text-xs font-semibold"
              value={skill.months}
              onChange={(e) => set({ months: e.target.value })}
            />
            <span className="text-[11.5px] text-muted-foreground">m</span>
          </div>
        </div>

        <div className="flex items-center gap-2.5">
          <span className="text-[10px] font-[650] uppercase tracking-[0.05em] text-muted-foreground">
            Proficiency
          </span>
          <StarRating
            value={skill.proficiency}
            onChange={(proficiency) => set({ proficiency })}
            label={`${skill.name} — proficiency`}
          />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2.5">
        <span className="text-[10px] font-[650] uppercase tracking-[0.05em] text-muted-foreground">
          Last used
        </span>
        <div className="flex flex-wrap gap-1">
          {LAST_USED_OPTIONS.map((option) => {
            const selected = skill.lastUsed === option.value;
            let toneCls =
              'border-input bg-card text-muted-foreground hover:border-primary hover:text-primary';
            if (selected && option.stale) {
              toneCls =
                'border-amber-400 bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400';
            } else if (selected) {
              toneCls =
                'border-primary bg-brand-bg-subtle text-primary font-[650]';
            }
            return (
              <button
                key={option.value}
                type="button"
                onClick={() => set({ lastUsed: selected ? '' : option.value })}
                aria-pressed={selected}
                className={cn(
                  'rounded-[6px] border px-[9px] py-[5px] text-[11px] font-[550] transition-colors',
                  toneCls
                )}
              >
                {option.label}
              </button>
            );
          })}
        </div>
      </div>

      {incomplete && (
        <p className="mt-2.5 flex items-center gap-1.5 text-[11px] font-[550] text-amber-700 dark:text-amber-400">
          <AlertTriangle className="h-3 w-3 shrink-0" />
          Add {missingSkillFields(skill).join(' and ')} to save this skill.
        </p>
      )}
    </div>
  );
};

SkillCard.propTypes = {
  skill: PropTypes.shape({
    name: PropTypes.string.isRequired,
    required: PropTypes.bool,
    years: PropTypes.string,
    months: PropTypes.string,
    proficiency: PropTypes.number,
    lastUsed: PropTypes.string,
    expectedProficiency: PropTypes.string,
    expectedExperienceMonths: PropTypes.number,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
};

// "n" count chip on each `.skl-ghead` group label, matching the mockup's
// `.skl-ghead .n` — the same mono/pill treatment TabCount uses for the tab
// strip, minus the "active tab" tint this one has no equivalent of.
const GroupCount = ({ done, total }) => (
  <span
    className={cn(
      'font-mono text-[10px] font-bold px-1.5 py-[2px] rounded-full',
      total > 0 && done === total
        ? 'bg-green-100 dark:bg-green-950/50 text-green-800 dark:text-green-400'
        : 'bg-muted text-muted-foreground'
    )}
  >
    {done}/{total}
  </span>
);

GroupCount.propTypes = {
  done: PropTypes.number.isRequired,
  total: PropTypes.number.isRequired,
};

// The Skills pane itself. Two groups when a live skill-match is in play —
// "Required for this role" (from GET .../skill-match, always shown even
// where the candidate has nothing yet — that absence is the thing worth
// asking about) and "Their other skills" (the API's own extra_core_skills
// plus anything typed in on the call) — collapsing to a single flat,
// manual-only list (the original behaviour) when there's no required-skill
// data at all, i.e. a bulk capture or a still-loading/failed fetch.
const SkillsField = ({
  skills,
  onChange,
  isLoading,
  isError,
  isPrefillable,
}) => {
  const [customInput, setCustomInput] = useState('');
  const required = skills.filter((s) => s.required);
  const own = skills.filter((s) => !s.required);
  const hasGroups = required.length > 0;
  // Drives both the summary banner below and (via the same skillStarted/
  // skillFilled pair) each SkillCard's own "Incomplete" flag — Save skills
  // stays blocked in the parent while this is non-empty, see
  // hasIncompleteSkills there.
  const incompleteSkills = skills.filter(
    (s) => skillStarted(s) && !skillFilled(s)
  );

  const addSkill = () => {
    const name = customInput.trim();
    if (!name) return;
    const exists = skills.some(
      (s) => s.name.toLowerCase() === name.toLowerCase()
    );
    if (!exists) onChange([...skills, emptySkill(name)]);
    setCustomInput('');
  };

  const updateSkillByName = (name, next) =>
    onChange(skills.map((s) => (s.name === name ? next : s)));

  const addInput = (
    <span className="mt-[9px] inline-flex items-center rounded-lg border border-dashed border-input bg-card pl-3 pr-1">
      <input
        type="text"
        value={customInput}
        onChange={(e) => setCustomInput(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            addSkill();
          }
        }}
        placeholder="Add a skill they mentioned…"
        aria-label="Add a skill not on the CV"
        className="w-[210px] bg-transparent py-2 text-[12.5px] outline-none placeholder:text-muted-foreground"
      />
    </span>
  );

  return (
    <div className="space-y-3.5">
      <p className="text-[11.5px] leading-[1.5] text-muted-foreground bg-muted rounded-lg px-[11px] py-[9px]">
        <b className="font-[650] text-foreground">Not required by default</b> —
        fill this in only if a skill came up on the call. Anything you enter is
        kept on the candidate&apos;s profile.
      </p>

      {isPrefillable && isLoading && (
        <p className="flex items-center gap-2 text-[11.5px] text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Loading this candidate&apos;s skill match…
        </p>
      )}
      {isPrefillable && isError && (
        <p className="flex items-center gap-2 text-[11.5px] text-amber-700 dark:text-amber-400">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
          Couldn&apos;t load this candidate&apos;s skill match — you can still
          add skills manually below.
        </p>
      )}
      {incompleteSkills.length > 0 && (
        <p className="flex items-center gap-2 rounded-lg bg-amber-50 px-[11px] py-[9px] text-[11.5px] font-[550] text-amber-800 dark:bg-amber-950/30 dark:text-amber-400">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
          {incompleteSkills.length === 1
            ? `“${incompleteSkills[0].name}” isn't`
            : `${incompleteSkills.length} skills aren't`}{' '}
          fully filled in — finish or clear{' '}
          {incompleteSkills.length === 1 ? 'it' : 'them'} before saving.
        </p>
      )}

      {hasGroups ? (
        <>
          <div>
            <div className="flex items-baseline gap-2 mb-2">
              <span className="text-[10.5px] font-[650] uppercase tracking-[0.05em] text-muted-foreground">
                Required for this role
              </span>
              <GroupCount
                done={required.filter(skillFilled).length}
                total={required.length}
              />
            </div>
            <div className="space-y-[9px]">
              {required.map((skill) => (
                <SkillCard
                  key={skill.name}
                  skill={skill}
                  onChange={(next) => updateSkillByName(skill.name, next)}
                />
              ))}
            </div>
          </div>

          <div className="pt-3.5 border-t border-border">
            <div className="flex items-baseline gap-2 mb-2">
              <span className="text-[10.5px] font-[650] uppercase tracking-[0.05em] text-muted-foreground">
                Their other skills
              </span>
              <GroupCount
                done={own.filter(skillFilled).length}
                total={own.length}
              />
            </div>
            {own.length > 0 && (
              <div className="space-y-[9px] mb-[9px]">
                {own.map((skill) => (
                  <SkillCard
                    key={skill.name}
                    skill={skill}
                    onChange={(next) => updateSkillByName(skill.name, next)}
                  />
                ))}
              </div>
            )}
            {addInput}
          </div>
        </>
      ) : (
        <>
          {own.length === 0 ? (
            <p className="text-[11.5px] text-muted-foreground border border-dashed border-border rounded-[10px] px-[13px] py-[11px]">
              Nothing added yet.
            </p>
          ) : (
            <div className="space-y-[9px]">
              {own.map((skill) => (
                <SkillCard
                  key={skill.name}
                  skill={skill}
                  onChange={(next) => updateSkillByName(skill.name, next)}
                />
              ))}
            </div>
          )}
          {addInput}
        </>
      )}
    </div>
  );
};

SkillsField.propTypes = {
  skills: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      required: PropTypes.bool,
      years: PropTypes.string,
      months: PropTypes.string,
      proficiency: PropTypes.number,
      lastUsed: PropTypes.string,
      expectedProficiency: PropTypes.string,
      expectedExperienceMonths: PropTypes.number,
      type: PropTypes.string,
    })
  ).isRequired,
  onChange: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  isError: PropTypes.bool,
  isPrefillable: PropTypes.bool,
};

SkillsField.defaultProps = {
  isLoading: false,
  isError: false,
  isPrefillable: false,
};

const UpdateProfileDetailsModal = ({
  open,
  onOpenChange,
  candidateCount,
  secMappingId,
  candidateId,
  opportunityId,
  assignmentId,
  isSubmitting,
  onSubmit,
}) => {
  const [form, setForm] = useState(emptyForm);
  const [activePane, setActivePane] = useState('details');
  // Genuinely locks the Skills tab out until Details has been saved at
  // least once in THIS opening of the modal — per explicit request, not
  // just visually de-emphasized while still clickable (which is what this
  // modal did before). Reset to false every time the modal opens, even for
  // a candidate whose profile already has saved details from a prior
  // session — there's no "already persisted" signal available here to
  // unlock it early against, so every opening starts from the same Details-
  // first rule rather than guessing.
  const [detailsSaved, setDetailsSaved] = useState(false);
  const locationMap = useLocationMap();
  const { toast } = useToast();
  const updateSkillMatch = useUpdateCandidateSkillMatch();

  // Single-candidate only (see the file-level comment) — a bulk capture gets
  // secMappingId={null} from GenericFlowAction and this stays disabled, so
  // the Skills tab falls back to its flat, manual-entry-only rendering.
  const {
    data: skillMatchResponse,
    isLoading: skillMatchLoading,
    isError: skillMatchError,
  } = useCandidateSkillMatch(secMappingId, {
    enabled: open && !!secMappingId,
  });
  const skillMatchData = skillMatchResponse?.data;

  // Details-tab prefill source — same single-candidate gating as skill-match
  // above (candidateId comes through null for a bulk capture — see
  // GenericFlowAction's own comment). opportunityId is optional for this
  // endpoint (createEndpoint drops the query segment if absent) but passed
  // through whenever the caller has it, same as CandidateProfileModal's own
  // usage of this same hook/endpoint.
  const { data: profileResponse, isLoading: profileLoading } =
    useCandidateProfile(candidateId, opportunityId, {
      enabled: open && !!candidateId,
    });
  const profileData = profileResponse?.success
    ? profileResponse.data.profile_data
    : null;

  // Which of the reference location table's entries are this requirement's
  // own advertised locations — GET .../assignment-locations (the exact same
  // call/hook AssignmentPipeline.jsx's own location selector already uses),
  // gated on assignmentId the same way the two prefills above gate on
  // candidateId/secMappingId. Matched by location_id: confirmed live this is
  // the same numeric id the reference-data locations list itself uses (e.g.
  // {"location_id":614,"location_name":"Mumbai"} for this exact
  // requirement) — not the requirement_location_id AssignmentPipeline.jsx
  // uses for its own location *selector* (a different id, scoped to the
  // requirement-location pairing, not the location itself). Stringified
  // since LocationPills' option values are, per Object.entries's own
  // string-keys behaviour (see locationOptions below).
  const { data: locationsResponse, isLoading: locationsLoading } =
    useRequirementLocations(null, {
      assignmentId,
      enabled: open && !!assignmentId,
    });
  const advertisedLocationIds = new Set(
    (locationsResponse?.data?.assignment_locations || []).map((l) =>
      String(l.location_id)
    )
  );

  // Guards each prefill so it fires once per open, right when its own fetch
  // resolves — not on every render, and not again if either happened to
  // refetch while the TA is mid-edit (e.g. a window refocus). Separate refs
  // since the two prefills come from two independent, differently-timed
  // fetches (skill-match vs. profile) and shouldn't gate each other.
  const prefilledSkillsRef = useRef(false);
  const prefilledDetailsRef = useRef(false);

  // Tracks whether the TA has actually typed/picked anything since the
  // modal opened — set only inside setField below, so the Skills/Details
  // prefills (setForm calls driven by server data, not by the TA) never
  // trip it. Backs the onPointerDownOutside guard on DialogContent further
  // down: an accidental click outside a form the TA has started filling in
  // shouldn't silently discard it the way an untouched/already-submitted
  // one safely can.
  const isDirtyRef = useRef(false);

  useEffect(() => {
    if (open) {
      setForm(emptyForm);
      setActivePane('details');
      prefilledSkillsRef.current = false;
      prefilledDetailsRef.current = false;
      isDirtyRef.current = false;
      setDetailsSaved(false);
    }
  }, [open]);

  useEffect(() => {
    if (open && secMappingId && skillMatchData && !prefilledSkillsRef.current) {
      prefilledSkillsRef.current = true;
      setForm((prev) => ({
        ...prev,
        skills: buildSkillsFromMatch(skillMatchData),
      }));
    }
  }, [open, secMappingId, skillMatchData]);

  useEffect(() => {
    // Wait out the requirement-locations fetch too (only when there's an
    // assignmentId to fetch for) so the advertised locations are actually
    // available to merge in — otherwise this fires on profileData alone,
    // marks itself done, and the advertised ticks never land. locationsLoading
    // is false on both success and error, so a failed locations fetch just
    // degrades to "prefill the candidate's own locations only", never blocks.
    const waitingOnLocations = !!assignmentId && locationsLoading;
    if (
      open &&
      candidateId &&
      profileData &&
      !waitingOnLocations &&
      !prefilledDetailsRef.current
    ) {
      prefilledDetailsRef.current = true;
      setForm((prev) => ({
        ...prev,
        ...buildDetailsFromProfile(
          profileData,
          locationMap,
          advertisedLocationIds
        ),
      }));
    }
    // locationMap / advertisedLocationIds are read at call time — locationMap
    // is effectively-static reference data, advertisedLocationIds is a fresh
    // Set each render but reflects resolved data once locationsLoading is
    // false (the real gate). Excluded from deps deliberately.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, candidateId, profileData, locationsLoading]);

  const locationOptions = Object.entries(locationMap)
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const setField = (name, value) => {
    isDirtyRef.current = true;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  // The CTC inputs' own `min`/`max` attributes only affect the native
  // stepper arrows and form-level constraint validation — a number input
  // still happily accepts a typed/pasted value outside that range (e.g.
  // "150" or "-5"), so the 0–100 LPA bound needs enforcing here too. Lets
  // an empty value through (clearing the field) and rejects any keystroke
  // that would land outside [0, 100] rather than clamping to the nearest
  // bound — rejecting means a candidate's own answer can never be silently
  // rewritten to a different number than what the TA actually typed.
  const setCtcField = (name, rawValue) => {
    if (rawValue === '') {
      setField(name, '');
      return;
    }
    const n = Number(rawValue);
    if (Number.isNaN(n) || n < 0 || n > 100) return;
    setField(name, rawValue);
  };

  const detailsFilledCount = [
    !!form.currentCtc,
    !!form.expectedCtc,
    form.noticeDays !== '',
    !!(form.expYears || form.expMonths),
    form.locations.length > 0,
  ].filter(Boolean).length;
  const skillsFilledCount = form.skills.filter(skillFilled).length;

  // "A blank field means they didn't get to it, not that it has no value" —
  // so nothing here is required, but there has to be at least one actual
  // answer or Save would just be a no-op call. A skill that only got a name
  // typed in (no rating, no last-used) doesn't count on its own — same as
  // the mockup, which drops those on save. Tracked per pane now that each
  // has its own Save button and its own backend call.
  const hasDetailsValue =
    form.currentCtc ||
    form.expectedCtc ||
    form.noticeDays !== '' ||
    form.expYears ||
    form.expMonths ||
    form.locations.length > 0 ||
    form.notes.trim();
  // Tightened to skillFilled's own definition (proficiency AND lastUsed,
  // matching the "Captured" badge/tab-count logic already used elsewhere in
  // this pane) now that the PATCH contract requires both `proficiency` (a
  // real enum, no "unrated" value) and `recency` on every skill it's sent —
  // a skill with only years/months typed in has neither yet, so there's no
  // longer a valid payload to send for it. Was the looser "any field
  // touched" filter under the old, more permissive POST contract.
  const filledSkills = form.skills.filter(skillFilled);
  const hasSkillsValue = filledSkills.length > 0;
  // Per explicit request: a skill that's been started (any field touched)
  // but isn't fully filled in blocks the whole Save, rather than being
  // silently excluded from filledSkills the way skipping a skill entirely
  // already safely is. See skillStarted/SkillCard's own "Incomplete" flag.
  const hasIncompleteSkills = form.skills.some(
    (s) => skillStarted(s) && !skillFilled(s)
  );

  const buildDetailsPayload = () => {
    const parsedData = {};
    // Form fields are LPA; this submit endpoint's parsed_data still wants
    // plain rupees (POST .../flow-actions/interview_profile_update — a
    // different endpoint than the GET profile prefill above, whose own
    // current_ctc/expected_ctc switched to LPA — see buildDetailsFromProfile's
    // comment; no evidence this submit contract changed too).
    if (form.currentCtc) {
      parsedData.current_ctc = Number(form.currentCtc) * 100000;
    }
    if (form.expectedCtc) {
      parsedData.expected_ctc = Number(form.expectedCtc) * 100000;
    }
    if (form.noticeDays !== '') {
      parsedData.notice_period = Number(form.noticeDays);
    }
    if (form.expYears || form.expMonths) {
      parsedData.total_work_experience =
        Number(form.expYears || 0) * 12 + Number(form.expMonths || 0);
    }
    if (form.locations.length > 0) {
      parsedData.preferred_work_locations = form.locations.map((l) => l.value);
    }
    if (form.notes.trim()) parsedData.notes = form.notes.trim();
    return { section: 'interview_profile_update', parsed_data: parsedData };
  };

  // Doesn't close the dialog — this is one of two independent saves in the
  // same modal (see the file-level comment), and the Skills tab is still
  // sitting right there unsaved. GenericFlowAction's handleModalSubmit
  // closes by default; { closeOnSubmit: false } opts out of that just for
  // this call, and switching to the Skills tab afterwards is what actually
  // moves the TA forward instead of leaving them staring at the same pane.
  // Switches regardless of whether the save itself succeeded or failed —
  // matches runAction's own existing behavior (it toasts either way but
  // never throws back to the caller, so there's nothing here to branch on;
  // a failed save's toast stays on screen after the tab switches). Skills
  // unlocks on the same "the call was made" basis, for the same reason —
  // there's no success/failure signal available here to gate on instead.
  const handleSubmitDetails = async () => {
    if (!hasDetailsValue) return;
    await onSubmit(buildDetailsPayload(), { closeOnSubmit: false });
    setDetailsSaved(true);
    setActivePane('skills');
  };

  // Payload contract, updated — method changed POST → PATCH, and the shape
  // changed with it (confirmed live via a real 422 against the old shape):
  //   PATCH .../skill-match
  //   { skills: [{ name, type, proficiency, experience_months, recency }] }
  // proficiency is now a real enum (BEGINNER/INTERMEDIATE/ADVANCED/EXPERT),
  // not the 0–4 number the old contract took — PROFICIENCY_LABEL_BY_RANK
  // reverse-maps this form's stars back to it; always resolves since
  // filledSkills (via skillFilled) guarantees proficiency is 1–4 here, never
  // 0. experience_months replaces the separate years/months fields. recency
  // is new — confirmed live (via a real 422 against a first guess: that it
  // reused this form's own lastUsed bucket strings verbatim; it doesn't) to
  // be its own enum, CURRENT/WITHIN_1Y/WITHIN_3Y/OVER_3Y — mapped from
  // lastUsed via RECENCY_BY_LAST_USED. Always resolves since filledSkills
  // (via skillFilled) guarantees lastUsed is one of the four bucket values.
  //
  // type is new too, and only confirmed for skills sourced from the
  // skill-match GET (skillFromApiItem carries the response's own `type`
  // straight through). A skill the TA typed in by hand via "Add a skill
  // they mentioned" has no reference row to read a type from — falls back
  // to DOMAIN_SKILL (the most common category in real responses) rather
  // than omitting the field and guaranteeing a 422, but this fallback is
  // NOT confirmed against the real enum; flag if a manually-added skill's
  // save starts failing.
  const handleSubmitSkills = async () => {
    // hasIncompleteSkills mirrors the Save button's own disabled state
    // (below) — kept here too since a disabled native button can't be
    // clicked, but this guard is what actually stops the save if that
    // ever gets out of sync.
    if (!hasSkillsValue || !secMappingId || hasIncompleteSkills) return;
    const skills = filledSkills.map((s) => ({
      name: s.name,
      type: s.type || 'DOMAIN_SKILL',
      proficiency: PROFICIENCY_LABEL_BY_RANK[s.proficiency],
      experience_months: Number(s.years || 0) * 12 + Number(s.months || 0),
      recency: RECENCY_BY_LAST_USED[s.lastUsed],
    }));
    try {
      await updateSkillMatch.mutateAsync({ secMappingId, skills });
      toast({ message: 'Skills saved', variant: 'success' });
      onOpenChange(false);
    } catch (error) {
      toast({
        message:
          error?.response?.data?.message ||
          error?.message ||
          'Failed to save skills',
        variant: 'destructive',
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-[640px]"
        // Per explicit request: a click outside shouldn't blow away
        // unsaved input on a form the TA has actually started filling in
        // (see isDirtyRef above) — Escape and the explicit Cancel/X still
        // close it regardless, only the easy-to-trigger-by-accident
        // outside-click is guarded.
        onPointerDownOutside={(e) => {
          if (isDirtyRef.current) e.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>Update Profile Details</DialogTitle>
          <DialogDescription>
            Capture what the candidate shared on the call. Applies to{' '}
            {candidateCount} selected candidate
            {candidateCount === 1 ? '' : 's'}.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="p-0">
          {/* Bespoke underline tab strip, matching the mockup's `.mtabs`/
              `.mtab` exactly (gap-20px, 2px underline, mono count pill) —
              full-bleed against the dialog's edges rather than sitting
              inside the body's own padding, same as the mockup's own
              `.mtabs { padding: 0 18px }` running edge to edge under the
              header. Both panes stay mounted (toggled via `hidden`, not
              unmounted) — matches the mockup's own reasoning: switching
              tabs must never discard a keystroke. */}
          <div
            role="tablist"
            aria-label="Capture sections"
            className="flex items-stretch gap-5 border-b border-border bg-muted px-[22px]"
          >
            <button
              type="button"
              role="tab"
              onClick={() => setActivePane('details')}
              aria-selected={activePane === 'details'}
              className={cn(
                'inline-flex items-center gap-[7px] whitespace-nowrap border-b-2 pb-[9px] pt-[10px] text-[12.5px] font-[600] transition-colors',
                activePane === 'details'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground-strong'
              )}
            >
              Details
              <TabCount
                done={detailsFilledCount}
                total={DETAILS_FIELD_COUNT}
                active={activePane === 'details'}
              />
            </button>
            <button
              type="button"
              role="tab"
              onClick={() => {
                if (detailsSaved) setActivePane('skills');
              }}
              disabled={!detailsSaved}
              aria-disabled={!detailsSaved}
              title={
                detailsSaved ? undefined : 'Save details first to unlock Skills'
              }
              aria-selected={activePane === 'skills'}
              className={cn(
                'inline-flex items-center gap-[7px] whitespace-nowrap border-b-2 pb-[9px] pt-[10px] text-[12.5px] font-[600] transition-colors',
                !detailsSaved && 'cursor-not-allowed opacity-50',
                activePane === 'skills'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground-strong'
              )}
            >
              Skills
              <TabCount
                done={skillsFilledCount}
                total={form.skills.length}
                active={activePane === 'skills'}
              />
              <span className="text-[9px] font-bold uppercase tracking-[0.05em] text-muted-foreground/70">
                Optional
              </span>
            </button>
          </div>

          <div
            role="tabpanel"
            className={cn(
              'space-y-4 px-[22px] py-[18px]',
              activePane !== 'details' && 'hidden'
            )}
          >
            {candidateId && profileLoading && (
              <p className="flex items-center gap-2 text-[11.5px] text-muted-foreground">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Loading this candidate&apos;s profile…
              </p>
            )}

            {/* Row 1 — the two CTC figures, same pairing as the mockup's
                first fld-2 row. */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-[7px]">
                <Label htmlFor="update-current-ctc" className={FIELD_LABEL_CLS}>
                  Current CTC
                </Label>
                <Input
                  id="update-current-ctc"
                  type="number"
                  min="0"
                  max="100"
                  step="0.5"
                  inputMode="decimal"
                  placeholder="₹ LPA"
                  value={form.currentCtc}
                  onChange={(e) => setCtcField('currentCtc', e.target.value)}
                />
              </div>
              <div className="space-y-[7px]">
                <Label
                  htmlFor="update-expected-ctc"
                  className={FIELD_LABEL_CLS}
                >
                  Expected CTC
                </Label>
                <Input
                  id="update-expected-ctc"
                  type="number"
                  min="0"
                  max="100"
                  step="0.5"
                  inputMode="decimal"
                  placeholder="₹ LPA"
                  value={form.expectedCtc}
                  onChange={(e) => setCtcField('expectedCtc', e.target.value)}
                />
              </div>
            </div>

            {/* Row 2 — notice period beside total experience, same pairing
                  as the mockup's second fld-2 row. */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-[7px]">
                <span className={FIELD_LABEL_CLS}>Notice period</span>
                <Select
                  value={form.noticeDays}
                  onValueChange={(value) => setField('noticeDays', value)}
                >
                  <SelectTrigger id="update-notice" aria-label="Notice period">
                    <SelectValue placeholder="Not given yet" />
                  </SelectTrigger>
                  <SelectContent>
                    {NOTICE_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-[7px]">
                <span className={FIELD_LABEL_CLS}>Total experience</span>
                <div className="flex items-center gap-1.5">
                  <Input
                    type="number"
                    min="0"
                    max="45"
                    aria-label="Years of experience"
                    className="w-16 text-center"
                    value={form.expYears}
                    onChange={(e) => setField('expYears', e.target.value)}
                  />
                  <span className="text-xs text-muted-foreground">yrs</span>
                  <Input
                    type="number"
                    min="0"
                    max="11"
                    aria-label="Months of experience"
                    className="w-16 text-center"
                    value={form.expMonths}
                    onChange={(e) => setField('expMonths', e.target.value)}
                  />
                  <span className="text-xs text-muted-foreground">mo</span>
                </div>
              </div>
            </div>

            <div className="space-y-[7px]">
              <span className={FIELD_LABEL_CLS}>Preferred locations</span>
              <LocationPills
                options={locationOptions}
                value={form.locations}
                onChange={(value) => setField('locations', value)}
                advertisedIds={advertisedLocationIds}
              />
              {advertisedLocationIds.size > 0 && (
                <p className="flex items-center gap-1 text-[11px] text-muted-foreground">
                  <Star className="h-3 w-3 fill-current" strokeWidth={1.8} />
                  Advertised on this requirement
                </p>
              )}
            </div>

            <div className="space-y-[7px]">
              <Label htmlFor="update-notes" className={FIELD_LABEL_CLS}>
                What they said
              </Label>
              <Textarea
                id="update-notes"
                rows={2}
                placeholder="Anything worth carrying into the interview brief"
                value={form.notes}
                onChange={(e) => setField('notes', e.target.value)}
              />
            </div>
          </div>

          <div
            role="tabpanel"
            className={cn(
              'px-[22px] py-[18px]',
              activePane !== 'skills' && 'hidden'
            )}
          >
            <SkillsField
              skills={form.skills}
              onChange={(skills) => setField('skills', skills)}
              isLoading={skillMatchLoading}
              isError={skillMatchError}
              isPrefillable={!!secMappingId}
            />
            {!secMappingId && (
              <p className="mt-3 text-[11.5px] text-muted-foreground">
                Skills save separately and need exactly one candidate selected —
                select just this candidate to save skills.
              </p>
            )}
          </div>
        </DialogBody>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting || updateSkillMatch.isPending}
          >
            Cancel
          </Button>
          {activePane === 'details' ? (
            <Button
              onClick={handleSubmitDetails}
              disabled={!hasDetailsValue || isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Working...
                </>
              ) : (
                'Save details'
              )}
            </Button>
          ) : (
            <Button
              onClick={handleSubmitSkills}
              disabled={
                !hasSkillsValue ||
                !secMappingId ||
                updateSkillMatch.isPending ||
                hasIncompleteSkills
              }
            >
              {updateSkillMatch.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Working...
                </>
              ) : (
                'Save skills'
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

UpdateProfileDetailsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  candidateCount: PropTypes.number,
  // Single candidate's sec_mapping_id — only present when exactly one
  // candidate is selected (see GenericFlowAction). Gates the skill-match
  // fetch/prefill; null for a bulk capture.
  secMappingId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  // The same single candidate's own candidate_id (distinct from
  // secMappingId) — gates the Details-tab profile fetch/prefill the same
  // way; also null for a bulk capture.
  candidateId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  // Optional — only narrows the profile fetch to this requirement's own
  // view of the candidate (matches CandidateProfileModal's own usage);
  // createEndpoint drops the query segment entirely when absent.
  opportunityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  // Not single-candidate-gated like the two above — the requirement's own
  // advertised locations are the same regardless of how many candidates are
  // selected, so this comes through even for a bulk capture.
  assignmentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isSubmitting: PropTypes.bool,
  onSubmit: PropTypes.func.isRequired,
};

UpdateProfileDetailsModal.defaultProps = {
  candidateCount: 0,
  secMappingId: null,
  candidateId: null,
  opportunityId: undefined,
  assignmentId: undefined,
  isSubmitting: false,
};

export default UpdateProfileDetailsModal;
